import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { GraphQLTypes } from 'src/lib/graphqlTypes';

export const GraphIDAvailableQuery = gql`
  query GraphIDAvailableQuery($accountId: ID!, $graphId: ID!) {
    account(id: $accountId) {
      id
      graphIDAvailable(id: $graphId)
    }
  }
`;

interface Props extends GraphQLTypes.GraphIDAvailableQueryVariables {
  skip?: boolean;
}

export const useIsGraphIdAvailable = ({ skip, ...variables }: Props) => {
  const { data, loading = false } = useQuery<
    GraphQLTypes.GraphIDAvailableQuery,
    GraphQLTypes.GraphIDAvailableQueryVariables
  >(GraphIDAvailableQuery, {
    variables,
    skip,
  });

  return useMemo(
    () => ({
      isGraphIdAvailable: data?.account?.graphIDAvailable,
      loading,
    }),
    [data?.account?.graphIDAvailable, loading],
  );
};

export const useLazyIsGraphIdAvailable = () => {
  const [checkIsGraphIdAvailableQuery, { data, loading = false }] =
    useLazyQuery<
      GraphQLTypes.GraphIDAvailableQuery,
      GraphQLTypes.GraphIDAvailableQueryVariables
    >(GraphIDAvailableQuery);

  const checkIsGraphIdAvailable = useCallback(
    ({ accountId, graphId }: GraphQLTypes.GraphIDAvailableQueryVariables) => {
      return checkIsGraphIdAvailableQuery({
        variables: {
          accountId,
          graphId,
        },
        fetchPolicy: 'no-cache',
      });
    },
    [checkIsGraphIdAvailableQuery],
  );

  return useMemo(
    () => ({
      checkIsGraphIdAvailable,
      loading,
      isGraphIdAvailable: !!data?.account?.graphIDAvailable,
    }),
    [data?.account?.graphIDAvailable, loading, checkIsGraphIdAvailable],
  );
};
